import * as IoC from 'typescript-ioc';
import { action, runInAction } from 'mobx';
import {
    IAttendeeLiveSessionDTO,
    IAttendeeLiveSessionParams,
    IControlDAO,
    IUpdateMeetingAttendeeLiveDataResponse
} from '../../../../../../api/control.dao';
import { ParticipantsLiveFormTabBaseViewModel } from './partisipants-live-form-tab-base.viewmodel';
import { onPopulateAggregatedAttendeeLiveCounters, onSyncEvent } from '../../../../../../graphql/subscriptions';
import { subscribeAppsyncData } from '../../../../../../infrastructure/utils/appsync.utils';
import { IGNORE_ATTENDEES } from '../../../../../../api/organizations.dao';
import { IAttendeesLiveFormContainerProps } from '../attendees-live-form.container';
import { handleApiException } from '../../../../../global/modal/alerts-host/alert-templates';
import { DEFAULT_PAGE_SIZE } from '../../../../../../constants/pagination.contants';
import { IOnSyncEventSubscriptionResponse, OnSyncEventType } from '../../../../../../api/events.dao';
import { IApiDateConverter } from '../../../../../../infrastructure/api-date-converter';

class ParticipantsLiveFormJoinedTabViewModel extends ParticipantsLiveFormTabBaseViewModel<IAttendeeLiveSessionDTO> {
    constructor(
        props: IAttendeesLiveFormContainerProps,
        @IoC.Inject private readonly controlDAO: IControlDAO = IoC.Container.get(IControlDAO),
        @IoC.Inject private readonly apiDateConverter: IApiDateConverter = IoC.Container.get(IApiDateConverter),
    ) {
        super();
        super.initialize(props);
    }

    public subscribe = async (): Promise<void> => {
        const liveCountersSubscription = await subscribeAppsyncData<IUpdateMeetingAttendeeLiveDataResponse>(
            onPopulateAggregatedAttendeeLiveCounters,
            { meetingId: this.meetingId },
            data => {
                const { value: { data: { onPopulateAggregatedAttendeeLiveCounters: liveData } } } = data;
                this.updateMeetingLiveData(liveData.total);
            }
        );

        const syncEventSubscription = await subscribeAppsyncData<IOnSyncEventSubscriptionResponse>(
            onSyncEvent,
            { eventId: this.eventId },
            data => {
                const { value: { data: { onSyncEvent: syncEventData } } } = data;

                if (syncEventData.type !== OnSyncEventType.LIVE_SESSION_UPDATED) return;

                try {
                    const body = JSON.parse(syncEventData.body) as IAttendeeLiveSessionDTO;
                    this.onJoinParticipantListener(body);
                } catch (e) {
                    handleApiException(e);
                }
            }
        );

        this.subscription = {
            unsubscribe: () => {
                liveCountersSubscription?.unsubscribe?.();
                syncEventSubscription?.unsubscribe?.();
            }
        };
    };

    private onJoinParticipantListener = (attendeeNotification: IAttendeeLiveSessionDTO): void => {
        if (!attendeeNotification) return;

        runInAction(() => {
            const newAttendee: IAttendeeLiveSessionDTO = {
                id: `${attendeeNotification.connectionId}#${attendeeNotification.meetingId}#${attendeeNotification.attendeeEmail}`,
                connectionId: attendeeNotification.connectionId,
                meetingId: attendeeNotification.meetingId,
                attendeeEmail: attendeeNotification.attendeeEmail,
                attendeeFirstLastName: attendeeNotification.attendeeFirstLastName,
                joinTime: attendeeNotification.joinTime && this.apiDateConverter.apiStringToDate(`${attendeeNotification.joinTime}`),
                leftTime: attendeeNotification.leftTime && this.apiDateConverter.apiStringToDate(`${attendeeNotification.leftTime}`),
                meetingConnectionType: attendeeNotification.meetingConnectionType,
                organizationName: attendeeNotification.organizationName,
            };

            const existingIndex: number = this.findAttendeeIndexById(newAttendee.id);
            if (existingIndex >= 0) {
                this.updateAttendeeInParticipantsList(existingIndex, newAttendee);
                return;
            }

            this.updateParticipantsOnLastPageCount();

            if (this.numberOfElementsOnLastPage <= DEFAULT_PAGE_SIZE) {
                this.items = [...this.items, newAttendee];
            } else {
                this.shouldReFetchAttendees = true;
            }
        });
    };

    @action
    private updateParticipantsOnLastPageCount(): void {
        this.numberOfElementsOnLastPage += 1;
    };

    private findAttendeeIndexById(newAttendeeId: string): number {
        return this.items.findIndex(existAttendee => existAttendee.id === newAttendeeId);
    };

    private updateAttendeeInParticipantsList(index: number, updatedAttendee: IAttendeeLiveSessionDTO): void {
        const updatedList = [...this.items];
        updatedList[index] = updatedAttendee;

        this.items = updatedList;
    };

    public fetchParticipants = async (): Promise<void> => {
        runInAction(() => {
            this.fetchingParticipants = true;
        });

        try {
            const params: IAttendeeLiveSessionParams = this.buildParams(DEFAULT_PAGE_SIZE);
            const response = await this.controlDAO.getAttendeeLiveSessions(params);

            runInAction(() => {
                this.items = [...this.items, ...response.content];
                this.totalNumber = response.totalCount;
                this.numberOfElementsOnLastPage = response.content.length;
                this.loadMorePaginationToken = response.nextPageToken ?? '';
            });

        } catch (e) {
            handleApiException(e);
        } finally {
            runInAction(() => {
                this.fetchingParticipants = false;
            });
        }
    };

    public reFetchExistingParticipants = async (): Promise<void> => {
        const participantsToUpdate = this.items.length;

        runInAction(() => {
            this.loadMorePaginationToken = '';
            this.fetchingParticipants = true;
        });

        try {
            const params: IAttendeeLiveSessionParams = this.buildParams(participantsToUpdate);
            const response = await this.controlDAO.getAttendeeLiveSessions(params);

            runInAction(() => {
                this.items = response.content;
                this.totalNumber = response.totalCount;
                this.numberOfElementsOnLastPage = response.content.length;
                this.loadMorePaginationToken = response.nextPageToken ?? '';
                this.shouldReFetchAttendees = false;
            });

        } catch (e) {
            handleApiException(e);
        } finally {
            runInAction(() => {
                this.fetchingParticipants = false;
            });
        }
    };

    @action
    public loadMoreParticipants = async (): Promise<void> => {
        if (!this.hasMoreParticipants || this.fetchingParticipants) return;

        if (this.shouldReFetchAttendees) {
            await this.reFetchExistingParticipants();
        }

        await this.fetchParticipants();
    };

    private buildParams(pageSize: number): IAttendeeLiveSessionParams {
        return {
            accountId: this.accountId,
            eventId: this.eventId,
            meetingId: this.meetingId,
            status: '',
            ignoreAttendees: !IGNORE_ATTENDEES,
            'next-page-token': this.loadMorePaginationToken,
            'page-size': pageSize,
        };
    };
}

export { ParticipantsLiveFormJoinedTabViewModel };